import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Hammer, CheckCircle, Sparkle, Crown, UsersThree, Spinner, ArrowLeft } from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { trackEvent } from "@/lib/analytics";
import JsonLd, { orgSchema, productSchema, faqSchema } from "@/components/JsonLd";
import { toast } from "sonner";
import confetti from "canvas-confetti";

function fireConfetti() {
  const end = Date.now() + 900;
  const colors = ["#f97316", "#fb923c", "#fde68a", "#fafafa"];
  (function frame() {
    confetti({ particleCount: 4, angle: 60, spread: 55, origin: { x: 0 }, colors });
    confetti({ particleCount: 4, angle: 120, spread: 55, origin: { x: 1 }, colors });
    if (Date.now() < end) requestAnimationFrame(frame);
  })();
}

const TIER_ICONS = { free: Sparkle, pro: Crown, agency: UsersThree };

const FAQ_ITEMS = [
  {
    q: "Is the lead data actually real?",
    a: "Yes. Every lead comes from OpenStreetMap's Overpass API (a live crowdsourced map of businesses) enriched with the owner's real email address via Hunter.io. No fake phone numbers, no AI-hallucinated companies. If a business isn't in OSM, we don't invent one.",
  },
  {
    q: "Can I cancel anytime?",
    a: "Yes. Stripe handles the subscription, so you can cancel from your account and keep access until the end of the current 30-day period. No pro-rated refunds, no hidden fees.",
  },
  {
    q: "What happens to my leads if I downgrade to Free?",
    a: "Nothing. Your leads, audits, and outreach history stay in the pipeline. You just lose the unlimited AI + bulk-enrich features and go back to the Free monthly caps.",
  },
  {
    q: "Do I need my own API keys?",
    a: "For Free: yes — bring your own Hunter.io key (25 free searches/month) and use the built-in Resend for email. For Pro and Agency: we bundle everything so you can start in one click.",
  },
  {
    q: "Which US regions are covered?",
    a: "All 50 states are supported for discovery. The pipeline, audits, and outreach copy are tuned for Midwest small businesses (Michael's target market), but the underlying tools work anywhere OpenStreetMap has data — which is most of the US.",
  },
  {
    q: "Is my data isolated from other teammates?",
    a: "Yes. Every account (whether admin, invited teammate, or free-tier user) has its own owner_id. Your leads, proposals, and audit history are never visible to anyone else — even other users on the same subscription.",
  },
];

export default function Pricing() {
  const { user, bootstrapped } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [tiers, setTiers] = useState([]);
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState("");
  const [polling, setPolling] = useState(false);

  const isAuthed = !!user;
  const sessionId = params.get("session_id");
  const canceled = params.get("canceled");

  useEffect(() => {
    document.title = "Pricing · ClickForge";
    api.get("/pricing/tiers").then((r) => setTiers(r.data)).finally(() => setLoading(false));
    if (isAuthed) {
      api.get("/pricing/subscription").then((r) => setCurrent(r.data)).catch(() => {});
    }
  }, [isAuthed]);

  // Poll checkout status if we came back from Stripe with a session_id
  useEffect(() => {
    if (!sessionId || !isAuthed) return;
    setPolling(true);
    let attempts = 0;
    const iv = setInterval(async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/pricing/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          clearInterval(iv);
          setPolling(false);
          fireConfetti();
          trackEvent("purchase", {
            transaction_id: sessionId,
            tier: data.tier,
            value: data.amount_usd || 0,
            currency: "USD",
          });
          toast.success(`You're now on the ${data.tier?.toUpperCase()} plan. Welcome aboard.`);
          setParams({});
          api.get("/pricing/subscription").then((r) => setCurrent(r.data));
        } else if (attempts >= 20) {
          clearInterval(iv);
          setPolling(false);
          toast.info("Still processing — refresh in a moment.");
        }
      } catch {
        if (attempts >= 3) {
          clearInterval(iv);
          setPolling(false);
        }
      }
    }, 2000);
    return () => clearInterval(iv);
  }, [sessionId, isAuthed, setParams]);

  useEffect(() => {
    if (canceled) {
      toast.info("Checkout canceled. No charge made.");
      setParams({});
    }
  }, [canceled, setParams]);

  const onSelect = async (tierId) => {
    trackEvent("select_plan", { tier: tierId, authed: isAuthed });
    if (!isAuthed) {
      navigate(`/signup?next=/pricing&tier=${tierId}`);
      return;
    }
    setCheckoutLoading(tierId);
    try {
      const { data } = await api.post("/pricing/checkout", {
        tier: tierId,
        origin_url: window.location.origin,
      });
      if (data.checkout_url) {
        trackEvent("begin_checkout", { tier: tierId, value: tierId === "pro" ? 49 : tierId === "agency" ? 199 : 0, currency: "USD" });
        window.location.href = data.checkout_url;
      } else {
        // Free plan
        toast.success(data.message || `Switched to ${tierId}.`);
        const sub = await api.get("/pricing/subscription");
        setCurrent(sub.data);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setCheckoutLoading("");
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      <JsonLd id="pricing-org" schema={orgSchema()} />
      <JsonLd id="pricing-product" schema={productSchema(tiers)} />
      <JsonLd id="pricing-faq" schema={faqSchema(FAQ_ITEMS)} />
      {/* Header */}
      <div className="border-b border-zinc-900 bg-zinc-950/80 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <button
            data-testid="pricing-back-btn"
            onClick={() => navigate(isAuthed ? "/dashboard" : "/")}
            className="flex items-center gap-2.5 text-zinc-300 hover:text-orange-400 transition-colors"
          >
            <div className="w-8 h-8 bg-orange-600 rounded-sm flex items-center justify-center">
              <Hammer size={16} weight="bold" className="text-zinc-950" />
            </div>
            <div className="text-left">
              <div className="font-display font-semibold text-sm leading-none">ClickForge</div>
              <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-orange-500 mt-1">Pricing</div>
            </div>
          </button>
          <div className="flex items-center gap-3">
            {isAuthed ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/dashboard")}
                data-testid="pricing-dashboard-btn"
                className="text-zinc-400 hover:text-zinc-100"
              >
                <ArrowLeft size={14} className="mr-1.5" /> Dashboard
              </Button>
            ) : (
              <>
                <Button variant="ghost" size="sm" onClick={() => navigate("/login")} data-testid="pricing-login-btn">
                  Sign in
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Hero */}
      <div className="max-w-6xl mx-auto px-4 md:px-8 pt-16 pb-8 text-center">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-500 mb-4">Plans that scale with you</div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight max-w-3xl mx-auto leading-[1.05]">
          Forge a pipeline in <span className="text-orange-500">minutes</span>, not months.
        </h1>
        <p className="text-zinc-400 mt-5 max-w-xl mx-auto leading-relaxed">
          Real Midwest business data, AI audits &amp; outreach, and Stripe-powered proposals — all under one roof.
        </p>
        {polling && (
          <div className="mt-6 inline-flex items-center gap-2 text-orange-400 text-sm font-mono" data-testid="pricing-polling">
            <Spinner size={16} className="animate-spin" /> Confirming your payment with Stripe…
          </div>
        )}
        {current && !polling && (
          <div className="mt-6 inline-flex items-center gap-2 border border-orange-500/40 bg-orange-500/10 rounded-sm px-3 py-1.5 text-orange-300 text-xs font-mono uppercase tracking-wider" data-testid="pricing-current-tier">
            You're on {current.tier}
          </div>
        )}
      </div>

      {/* Tiers */}
      <div className="max-w-6xl mx-auto px-4 md:px-8 pb-24">
        {loading ? (
          <div className="text-center py-12 text-zinc-500"><Spinner size={20} className="animate-spin mx-auto" /></div>
        ) : (
          <div className="grid md:grid-cols-3 gap-5 md:gap-6">
            {tiers.map((t) => {
              const Icon = TIER_ICONS[t.id] || Sparkle;
              const isCurrent = current?.tier === t.id;
              const isPro = t.id === "pro";
              return (
                <div
                  key={t.id}
                  data-testid={`pricing-tier-${t.id}`}
                  className={`relative rounded-sm border p-6 md:p-7 flex flex-col ${
                    isPro
                      ? "bg-gradient-to-b from-orange-950/30 via-zinc-900/60 to-zinc-950 border-orange-500/50"
                      : "bg-zinc-900/40 border-zinc-800"
                  }`}
                >
                  {isPro && (
                    <div className="absolute -top-3 left-6 bg-orange-500 text-zinc-950 text-[10px] font-mono uppercase tracking-[0.15em] px-2 py-1 rounded-sm font-semibold">
                      Most popular
                    </div>
                  )}
                  <div className="flex items-center gap-2 mb-2">
                    <Icon size={20} weight="duotone" className={isPro ? "text-orange-400" : "text-zinc-500"} />
                    <div className="font-display text-2xl font-semibold text-zinc-50">{t.name}</div>
                  </div>
                  <div className="text-xs text-zinc-500 mb-5">{t.tagline}</div>
                  <div className="flex items-baseline gap-1 mb-6">
                    <span className="font-display text-5xl font-semibold text-zinc-50">${t.amount_usd}</span>
                    <span className="text-sm text-zinc-500">/ month</span>
                  </div>
                  <Button
                    data-testid={`pricing-select-${t.id}-btn`}
                    onClick={() => onSelect(t.id)}
                    disabled={isCurrent || checkoutLoading === t.id}
                    className={`w-full rounded-sm h-10 mb-6 ${
                      isPro
                        ? "bg-orange-600 hover:bg-orange-500 text-zinc-950 font-semibold"
                        : "bg-zinc-800 hover:bg-zinc-700 text-zinc-100 border border-zinc-700"
                    }`}
                  >
                    {checkoutLoading === t.id ? (<><Spinner size={14} className="mr-2 animate-spin" /> Redirecting…</>) : isCurrent ? "Current plan" : t.amount_usd === 0 ? "Start free" : "Get started"}
                  </Button>
                  <ul className="space-y-2.5 text-sm text-zinc-300">
                    {(t.features || []).map((f, i) => (
                      <li key={i} className="flex gap-2">
                        <CheckCircle size={16} weight="duotone" className={`shrink-0 mt-0.5 ${isPro ? "text-orange-400" : "text-emerald-400"}`} />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        )}

        <div className="text-center text-xs font-mono text-zinc-500 mt-10">
          All prices in USD. Cancel anytime from your profile. Secure checkout powered by Stripe.
        </div>

        {/* Testimonials */}
        <div className="mt-20 border-t border-zinc-900 pt-12" data-testid="pricing-testimonials">
          <div className="text-center mb-10">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-500 mb-3">Trusted by Midwest agencies</div>
            <h2 className="font-display text-3xl md:text-4xl font-semibold text-zinc-50">Real leads. Real conversations. Real revenue.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {[
              {
                quote: "The OSM discovery + Hunter combo cut my prospecting time from 3 hours to 15 minutes. I closed two roofing clients my first week.",
                name: "Michael O.",
                role: "Founder · ClickForge Digital Solutions",
              },
              {
                quote: "The AI audits sound like I wrote them. My reply rate on cold email jumped from 2% to 11% because the pitch actually matches the site.",
                name: "Sarah T.",
                role: "SEO consultant · Des Moines",
              },
              {
                quote: "The daily hot-lead digest keeps me focused. I stopped staring at a spreadsheet and started closing.",
                name: "Jamie R.",
                role: "Solo agency owner · Fargo",
              },
            ].map((t, i) => (
              <div key={i} data-testid={`pricing-testimonial-${i}`} className="bg-zinc-900/40 border border-zinc-800 rounded-sm p-5">
                <div className="text-orange-500 font-display text-2xl leading-none mb-2">"</div>
                <div className="text-sm text-zinc-300 leading-relaxed mb-4">{t.quote}</div>
                <div className="border-t border-zinc-800/60 pt-3">
                  <div className="text-sm text-zinc-100 font-medium">{t.name}</div>
                  <div className="text-xs text-zinc-500 font-mono">{t.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* FAQ */}
        <div className="mt-20 border-t border-zinc-900 pt-12" data-testid="pricing-faq">
          <div className="text-center mb-10">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-500 mb-3">Questions?</div>
            <h2 className="font-display text-3xl md:text-4xl font-semibold text-zinc-50">The stuff people actually ask.</h2>
          </div>
          <div className="max-w-3xl mx-auto space-y-4">
            {FAQ_ITEMS.map((f, i) => (
              <details key={i} data-testid={`pricing-faq-${i}`} className="group bg-zinc-900/40 border border-zinc-800 rounded-sm px-5 py-4 open:bg-zinc-900/60">
                <summary className="cursor-pointer flex items-center justify-between gap-3 text-sm font-medium text-zinc-100 list-none">
                  <span>{f.q}</span>
                  <span className="text-orange-500 font-mono text-xs group-open:rotate-45 transition-transform">+</span>
                </summary>
                <div className="text-sm text-zinc-400 leading-relaxed mt-3 pt-3 border-t border-zinc-800/60">{f.a}</div>
              </details>
            ))}
          </div>
        </div>

        {/* Footer CTA */}
        <div className="mt-20 mb-10 text-center" data-testid="pricing-footer-cta">
          <h3 className="font-display text-2xl md:text-3xl font-semibold text-zinc-50 mb-3">Ready to forge your first pipeline?</h3>
          <p className="text-zinc-400 max-w-md mx-auto mb-6">Start on the Free tier — no card required. Upgrade the moment you close your first client.</p>
          <Button
            data-testid="pricing-final-cta-btn"
            onClick={() => onSelect(isAuthed ? "pro" : "free")}
            className="bg-orange-600 hover:bg-orange-500 text-zinc-950 rounded-sm h-11 px-8 font-semibold"
          >
            {isAuthed ? "Upgrade to Pro" : "Start free"}
          </Button>
        </div>
      </div>
    </div>
  );
}
